import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Drawer, Box, Typography, TextField, Button, Stack, MenuItem, Divider, Alert, Chip, LinearProgress,
} from '@mui/material';
import { getPartnerGodView, type PartnerRow } from '../../services/partnerUserService';
import { SERVICE_HUB_OPTIONS, getPartnerConfig } from '../../constants/partnerServices';
import { getUserTypeFormConfig, PARTNER_USER_TYPES } from '../../constants/userManagement';
import PersonNameField from '../ui/forms/PersonNameField';
import PhoneWithDialCodeField from '../ui/forms/PhoneWithDialCodeField';
import {
  validatePartnerContactForm,
  partnerValidationSummary,
  clearPartnerFieldErrors,
} from '../../utils/partnerFormValidation';
import { PERSON_NAME_MIN, PERSON_NAME_MAX } from '../../utils/personFieldValidation';
import { composeE164, localPhoneDigits, splitPhoneByDialCodes } from '../../utils/phoneDialCode';
import { getCachedCountryDialCodes } from '../../services/countryDialCodeService';

interface ZoneOption { _id: string; name: string }

interface PartnerEditDrawerProps {
  open: boolean;
  onClose: () => void;
  partner: PartnerRow | null;
  zones: ZoneOption[];
  onSave: (body: Record<string, unknown>) => void;
  loading?: boolean;
}

function splitPartnerPhone(raw?: string, storedCode?: string): { phone: string; phoneCountryCode: string } {
  const dialCodes = getCachedCountryDialCodes().map((o) => o.dialCode);
  if (raw?.startsWith('+')) {
    const split = splitPhoneByDialCodes(raw, dialCodes);
    return {
      phoneCountryCode: storedCode || split.dialCode || '+91',
      phone: split.localNumber || localPhoneDigits(raw),
    };
  }
  return {
    phoneCountryCode: storedCode || '+91',
    phone: localPhoneDigits(raw || ''),
  };
}

export default function PartnerEditDrawer({ open, onClose, partner, zones, onSave, loading }: PartnerEditDrawerProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phoneCountryCode: '',
    zoneId: '',
    serviceHub: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: godView, isLoading: loadingDetail } = useQuery({
    queryKey: ['partner-edit-detail', partner?._id],
    queryFn: () => getPartnerGodView(partner!._id, { tab: 'wallet', page: 1, limit: 1 }),
    enabled: open && !!partner?._id,
  });

  const detailPartner = godView?.partner;
  const source: PartnerRow | null = partner
    ? {
        ...partner,
        firstName: detailPartner?.firstName || partner.firstName,
        lastName: detailPartner?.lastName || partner.lastName,
        email: detailPartner?.email || partner.email,
        phone: detailPartner?.phone || partner.phone,
        partnerStatus: detailPartner?.partnerStatus || partner.partnerStatus,
      }
    : null;

  const partnerEntityType = source?.partnerEntityType || source?.userType || '';
  const partnerConfig = getPartnerConfig(partnerEntityType);
  const requiresZone = PARTNER_USER_TYPES.has(partnerConfig?.userType || partnerEntityType)
    || getUserTypeFormConfig(partnerConfig?.userType || partnerEntityType).requiresZone;

  useEffect(() => {
    if (!open || !source) return;
    setErrors({});
    const meta = source.metadata as { phoneCountryCode?: string } | undefined;
    const phoneSplit = splitPartnerPhone(source.phone, meta?.phoneCountryCode);
    setForm({
      firstName: source.firstName || '',
      lastName: source.lastName || '',
      email: source.email || '',
      phone: phoneSplit.phone,
      phoneCountryCode: phoneSplit.phoneCountryCode,
      zoneId: source.zoneId?._id || '',
      serviceHub: source.serviceHub || '',
    });
  }, [open, source?._id, source?.firstName, source?.lastName, source?.email, source?.phone, source?.zoneId?._id, source?.serviceHub]);

  const scrollToFirstError = () => {
    requestAnimationFrame(() => {
      const first = formRef.current?.querySelector<HTMLElement>(
        '.Mui-error input, .Mui-error textarea, .Mui-error [role="combobox"]',
      );
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      first?.focus({ preventScroll: true });
    });
  };

  const patchErrors = (keys: string[]) => {
    setErrors((prev) => clearPartnerFieldErrors(prev, keys));
  };

  const validate = () => {
    const next = validatePartnerContactForm(form, { requiresZone, zones });
    setErrors(next);
    if (Object.keys(next).length) scrollToFirstError();
    return Object.keys(next).length === 0;
  };

  const validationLines = partnerValidationSummary(errors);
  const nameHelper = `Letters only · ${PERSON_NAME_MIN}–${PERSON_NAME_MAX} characters`;
  const statusLabel = String(source?.partnerStatus || '').replace(/_/g, ' ');

  if (!open || !partner) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 440 } } }}
    >
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>Edit Partner</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Update identity and assignment details for this partner account.
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {statusLabel && (
            <Chip size="small" label={statusLabel} sx={{ textTransform: 'capitalize' }} color="info" variant="outlined" />
          )}
          {source?.zoneId?.name && (
            <Chip size="small" label={`Zone: ${source.zoneId.name}`} variant="outlined" />
          )}
          {source?.serviceHub && (
            <Chip size="small" label={`Hub: ${source.serviceHub}`} variant="outlined" />
          )}
        </Stack>

        {loadingDetail && <LinearProgress sx={{ mb: 2 }} />}

        {validationLines.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Please fix the following ({validationLines.length}):
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {validationLines.map((line) => (
                <Typography key={line} component="li" variant="body2">{line}</Typography>
              ))}
            </Box>
          </Alert>
        )}

        <Stack
          component="form"
          ref={formRef}
          spacing={2}
          noValidate
          sx={{ flex: 1, overflow: 'auto' }}
          onSubmit={(e) => {
            e.preventDefault();
            if (!validate()) return;
            const phoneE164 = form.phone.trim()
              ? composeE164(form.phoneCountryCode, form.phone)
              : undefined;
            onSave({
              firstName: form.firstName.trim(),
              lastName: form.lastName.trim() || undefined,
              email: form.email.trim() || undefined,
              phone: phoneE164,
              zoneId: form.zoneId || undefined,
              serviceHub: form.serviceHub || undefined,
              metadata: {
                phoneCountryCode: form.phoneCountryCode || undefined,
              },
            });
          }}
        >
          <Typography variant="subtitle2" fontWeight={800}>Identity</Typography>
          <PersonNameField
            label="First Name"
            required
            fullWidth
            size="small"
            value={form.firstName}
            error={!!errors.firstName}
            helperText={errors.firstName || nameHelper}
            inputProps={{ maxLength: PERSON_NAME_MAX }}
            onChange={(e) => {
              patchErrors(['firstName']);
              setForm({ ...form, firstName: e.target.value });
            }}
          />
          <PersonNameField
            label="Last Name"
            fullWidth
            size="small"
            value={form.lastName}
            error={!!errors.lastName}
            helperText={errors.lastName || nameHelper}
            inputProps={{ maxLength: PERSON_NAME_MAX }}
            onChange={(e) => {
              patchErrors(['lastName']);
              setForm({ ...form, lastName: e.target.value });
            }}
          />

          <Typography variant="subtitle2" fontWeight={800} sx={{ pt: 1 }}>Contact</Typography>
          <TextField
            label="Email"
            type="email"
            required={!form.phone.trim()}
            fullWidth
            size="small"
            value={form.email}
            error={!!errors.email || !!errors.contact}
            helperText={
              errors.email
              || errors.contact
              || 'Required when phone is not provided'
            }
            onChange={(e) => {
              patchErrors(['email', 'contact']);
              setForm({ ...form, email: e.target.value });
            }}
          />
          <PhoneWithDialCodeField
            label="Phone"
            required={!form.email.trim()}
            phone={form.phone}
            countryCode={form.phoneCountryCode}
            preferredCountry="IN"
            error={!!errors.phone || !!errors.contact}
            helperText={errors.phone || errors.contact || 'Required when email is not provided'}
            onChange={({ phone, countryCode }) => {
              patchErrors(['phone', 'contact']);
              setForm({
                ...form,
                phone,
                phoneCountryCode: countryCode,
              });
            }}
          />

          <Typography variant="subtitle2" fontWeight={800} sx={{ pt: 1 }}>Assignment</Typography>
          <TextField
            select
            label="Zone"
            required={requiresZone}
            fullWidth
            size="small"
            value={form.zoneId}
            error={!!errors.zoneId}
            helperText={
              errors.zoneId
              || (requiresZone ? 'Required — assign the partner to a service zone' : '')
            }
            onChange={(e) => {
              patchErrors(['zoneId']);
              setForm({ ...form, zoneId: e.target.value });
            }}
          >
            <MenuItem value="" disabled={requiresZone}>
              {requiresZone ? 'Select a zone' : 'No zone'}
            </MenuItem>
            {zones.map((z) => <MenuItem key={z._id} value={z._id}>{z.name}</MenuItem>)}
          </TextField>
          <TextField
            select
            label="Service Hub"
            fullWidth
            size="small"
            value={form.serviceHub}
            onChange={(e) => setForm({ ...form, serviceHub: e.target.value })}
          >
            {SERVICE_HUB_OPTIONS.map((h) => <MenuItem key={h.value} value={h.value}>{h.label}</MenuItem>)}
          </TextField>

          <Divider sx={{ my: 0.5 }} />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button type="button" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading || loadingDetail}>
              {loading ? 'Saving…' : 'Save changes'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}
